import { useRole } from '@/services/authentication/hooks/user';
import { ContractStatus, Partner, PartnerCode, PartnerRegisteredInfo } from '../contracts/partners';
import { APIError } from '../errors/APIError';
import { useAPIMutation, useAPIQuery } from '../useAPIProvider';
import { useCompanies, useUpdateCompanyProperyBag } from './companies';

const mapPartnerEnums = (p: Partner): Partner => {
  if (typeof p.contractStatus === 'string') {
    p.contractStatus = ContractStatus[p.contractStatus as keyof typeof ContractStatus] ?? p.contractStatus;
  }
  return p;
};

export const usePartners = () => {
  return useAPIQuery(['getPartners'], async client => {
    const response = await client.partners.getPartners();
    if (response.status !== 200) throw APIError.fromResponse(response);
    return response.body.items.map(mapPartnerEnums).sort((a, b) => a.name.toLowerCase().localeCompare(b.name.toLowerCase()));
  });
};

export const usePartner = (id?: string) => {
  return useAPIQuery(['getPartner', id], async client => {
    if (!id) return null as unknown as ReturnType<typeof mapPartnerEnums>;
    const response = await client.partners.getPartner({ params: { id } });
    if (response.status !== 200) throw APIError.fromResponse(response);
    return mapPartnerEnums(response.body);
  });
};

export const useMSPPropertyBag = (mspId?: string) => {
  const role = useRole();
  // Consider cache key
  return useAPIQuery(['partnerPropertyBag', mspId], async client => {
    const id = mspId || role.partnerId;
    if (!id) throw new Error('Missing msp id');

    const response = await client.partners.getPartner({ params: { id } });
    if (response.status !== 200) throw APIError.fromResponse(response);
    return response.body.propertyBag;
  });
};

export const useGetPartnerContract = () => {
  return useAPIMutation(client => {
    return async ({ id }: { id: string }) => {
      const response = await client.partners.getPartnerContract({ params: { id } });
      if (response.status !== 200) throw APIError.fromResponse(response);
      return response.body;
    };
  });
};

export const useGetPartnerNda = () => {
  return useAPIMutation(client => {
    return async ({ id }: { id: string }) => {
      const response = await client.partners.getPartnerNda({ params: { id } });
      if (response.status !== 200) throw APIError.fromResponse(response);
      return response.body;
    };
  });
};

export interface CreatePartnerNdaArgs {
  regNumber: string;
  regAddress: string;
  signerEmail: string;
  signerName: string;
  signerPosition: string;
}

export const useCreatePartnerNda = (mspId: string) => {
  return useAPIMutation((client, query) => {
    return async (args: CreatePartnerNdaArgs) => {
      const response = await client.partners.createPartnerNda({
        body: {
          regNumber: args.regNumber,
          regAddress: args.regAddress,
          signerEmail: args.signerEmail,
          signerName: args.signerName,
          signerPosition: args.signerPosition,
          returnUrl: `${window.location.origin}/partnerOnboarding/msp/${mspId}/ndaCreated`
        },
        params: {
          id: mspId
        }
      });

      if (response.status !== 200) throw APIError.fromResponse(response);

      await query.invalidateQueries(['getPartners']);
      return response.body;
    };
  });
};

export const useCreatePartnerContract = (mspId: string) => {
  return useAPIMutation((client, query) => {
    return async () => {
      const response = await client.partners.createPartnerContract({
        body: {
          returnUrl: `${window.location.origin}/partnerOnboarding/msp/${mspId}/contractSigned`
        },
        params: {
          id: mspId
        }
      });

      if (response.status !== 200) throw APIError.fromResponse(response);

      await query.invalidateQueries(['getPartners']);
      return response.body;
    };
  });
};

export const useCompletePartnerContract = (mspId: string) => {
  return useAPIMutation((client, query) => {
    return async () => {
      const response = await client.partners.updatePartnerContract({
        body: {},
        params: {
          id: mspId
        }
      });

      if (response.status !== 200) throw APIError.fromResponse(response);

      await query.invalidateQueries(['getPartners']);
      return response.body;
    };
  });
};

export interface CreatePartnerArgs {
  name: string;
  code: string;
  regionId: string;
  registeredInfo: PartnerRegisteredInfo;
  allowCustomLogo: boolean;
}

export const useCreatePartner = () => {
  return useAPIMutation((client, query) => {
    return async ({ name, code, registeredInfo, allowCustomLogo, regionId }: CreatePartnerArgs) => {
      const response = await client.partners.createPartner({
        body: { name, code, registeredInfo, propertyBag: { allowCustomLogo }, regionId }
      });

      if (response.status !== 200) throw APIError.fromResponse(response);

      await query.invalidateQueries(['getPartners']);
      return response.body;
    };
  });
};

export interface CreatePartnerCodeArgs {
  names: string[];
}

export const useCreatePartnerCode = () => {
  return useAPIMutation(client => {
    return async ({ names }: CreatePartnerCodeArgs) => {
      const response = await client.partners.createPartnerCode({ body: { names } });
      if (response.status !== 200) throw APIError.fromResponse(response);
      return response.body.items as PartnerCode[];
    };
  });
};

interface DeletePartnerArgs {
  id: string;
}

interface UpdatePartnerArgs {
  name: string;
  code: string;
  disabled: boolean;
  regionId: string;
  registeredInfo: PartnerRegisteredInfo;
  allowCustomLogo: boolean;
  enableCustomNotifications: boolean;
}

export const useUpdatePartner = (id: string) => {
  const updateCompany = useUpdateCompanyProperyBag();
  const [companies] = useCompanies(id);

  return useAPIMutation((client, query) => {
    return async ({ name, code, disabled, registeredInfo, allowCustomLogo, regionId, enableCustomNotifications }: UpdatePartnerArgs) => {
      const response = await client.partners.updatePartner({
        params: { id },
        body: {
          name,
          code,
          disabled,
          registeredInfo,
          regionId,
          propertyBag: {
            allowCustomLogo
          },
          enableCustomNotifications
        }
      });

      if (response.status !== 200) throw APIError.fromResponse(response);

      // TODO better if we had a multi put/patch endpoint, but this will have to do.
      await Promise.all(
        companies.map(async company => {
          await updateCompany.mutateAsync({
            partnerId: id,
            company,
            allowCustomLogo,
            selectedProductIds: company.propertyBag.selectedProductIds || []
          });
        })
      );

      await query.invalidateQueries(['getPartners']);
      await query.invalidateQueries(['getPartner', id]);
      await query.invalidateQueries(['partnerPropertyBag', id]);
      await query.invalidateQueries(['getCompanies', id]);
      return response.body;
    };
  });
};

export const useDisablePartner = () => {
  return useAPIMutation((client, query) => {
    return async ({ id }: DeletePartnerArgs) => {
      const partner = await client.partners.getPartner({ params: { id } });
      if (partner.status !== 200) throw APIError.fromResponse(partner);
      const response = await client.partners.updatePartner({
        params: { id },
        body: {
          ...partner.body,
          regionId: partner.body.region.id,
          disabled: true
        }
      });
      if (response.status !== 200) throw APIError.fromResponse(response);
      await query.invalidateQueries(['getPartners']);
      await query.invalidateQueries(['getPartner', id]);
      return response.body;
    };
  });
};

export const useEnablePartner = () => {
  return useAPIMutation((client, query) => {
    return async ({ id }: DeletePartnerArgs) => {
      const partner = await client.partners.getPartner({ params: { id } });
      if (partner.status !== 200) throw APIError.fromResponse(partner);
      const response = await client.partners.updatePartner({
        params: { id },
        body: {
          ...partner.body,
          regionId: partner.body.region.id,
          disabled: false
        }
      });
      if (response.status !== 200) throw APIError.fromResponse(response);
      await query.invalidateQueries(['getPartners']);
      await query.invalidateQueries(['getPartner', id]);
      return response.body;
    };
  });
};

export const useDeletePartner = () => {
  return useAPIMutation(client => {
    return ({ id }: DeletePartnerArgs) => {
      return client.partners.deletePartner({ params: { id } });
    };
  });
};

export const usePartnerUsers = (id: string) => {
  return useAPIQuery(['getPartnerUsers', id], async client => {
    const response = await client.partners.getPartnerUsers({ params: { id } });
    if (response.status !== 200) throw APIError.fromResponse(response);
    return response.body;
  });
};

interface DeletePartnerUserArgs {
  partnerId: string;
  id: string;
}

export const useDeletePartnerUser = () => {
  return useAPIMutation(client => {
    return ({ partnerId, id }: DeletePartnerUserArgs) => {
      return client.partners.deletePartnerUser({ params: { partnerId, id } });
    };
  });
};

export const useGetPartnerHaloMapping = (id: string) => {
  return useAPIQuery(['getPartnerHaloMapping', id], async (client) => {
    const response = await client.partners.getPartnerHaloMapping({ params: { id } });
    if (response.status !== 200) throw APIError.fromResponse(response);
    return response.body;
  });
};

export const useUpdatePartnerHaloMapping = (id: string) => {
  return useAPIMutation((client, query) => {
    return async (haloClientId: string | null) => {
      const response = await client.partners.updatePartnerHaloMapping({
        params: { id },
        body: { haloClientId }
      });
      if (response.status !== 204 && response.status !== 200) throw APIError.fromResponse(response);
      await query.invalidateQueries(['getPartnerHaloMapping', id]);
      return response.body;
    };
  });
};
